import React, { useEffect, useMemo, useState } from 'react';
import {
  ArrowLeft,
  Check,
  ChevronDown,
  Copy,
  Download,
  FileCheck,
  FileText,
  Info,
  Lock,
  Plus,
  Printer,
  RefreshCw,
  SlidersHorizontal,
  Trash2,
  X,
} from 'lucide-react';

import ModalShell from './ModalShell';
import useToast from '../hooks/useToast';
import AutoResizeTextarea from './AutoResizeTextarea';
import useModalBackButton from '../hooks/useModalBackButton';
import {
  buildInvoiceAmountWords,
  formatInvoiceMoney,
  getInvoiceRowsTotal,
  parseInvoiceDecimal,
} from '../utils/invoiceDraft';

const DEFAULT_VAT_TEXT = 'Без налога (НДС)';
const DEFAULT_ACT_COMPLETION_NOTE =
  'Вышеперечисленные услуги выполнены полностью и в срок. Заказчик претензий по объему, качеству и срокам оказания услуг не имеет.';

const PRESET_UNITS = [
  { value: 'усл.', label: 'усл.', desc: 'Условная единица' },
  { value: 'шт.', label: 'шт.', desc: 'Штука' },
  { value: 'кг', label: 'кг', desc: 'Килограмм' },
  { value: 'компл.', label: 'компл.', desc: 'Комплект' },
  { value: 'м²', label: 'м²', desc: 'Квадратный метр' },
  { value: 'пог. м', label: 'пог. м', desc: 'Погонный метр' },
  { value: 'пара', label: 'пара', desc: 'Пара' },
];

const createEmptyRow = () => ({
  name: '',
  quantity: '1',
  unit: 'усл.',
  price: '0',
});

const normalizeRow = (row = {}) => ({
  name: String(row?.name ?? ''),
  quantity: String(row?.quantity ?? '1'),
  unit: row?.unit !== undefined && row?.unit !== null ? String(row.unit) : 'усл.',
  price: String(row?.price ?? '0'),
});

const normalizeDraft = (draft = {}) => {
  const rows = Array.isArray(draft?.rows) && draft.rows.length
    ? draft.rows.map(normalizeRow)
    : [createEmptyRow()];

  return {
    invoice_number: String(draft?.invoice_number || ''),
    header_note: String(draft?.header_note || ''),
    invoice_date: String(draft?.invoice_date || ''),
    bank_name: String(draft?.bank_name || ''),
    bik: String(draft?.bik || ''),
    corr_account: String(draft?.corr_account || ''),
    account: String(draft?.account || ''),
    supplier_inn: String(draft?.supplier_inn || ''),
    supplier_kpp: String(draft?.supplier_kpp || ''),
    supplier_name: String(draft?.supplier_name || ''),
    supplier_summary: String(draft?.supplier_summary || ''),
    buyer_name: String(draft?.buyer_name || ''),
    buyer_inn: String(draft?.buyer_inn || ''),
    buyer_kpp: String(draft?.buyer_kpp || ''),
    buyer_summary: String(draft?.buyer_summary || ''),
    vat_text: String(draft?.vat_text || DEFAULT_VAT_TEXT),
    director_label: String(draft?.director_label || 'Руководитель'),
    director_name: String(draft?.director_name || ''),
    accountant_label: String(draft?.accountant_label || 'Бухгалтер'),
    accountant_name: String(draft?.accountant_name || ''),
    amount_in_words: String(draft?.amount_in_words || ''),
    rows,
  };
};

const normalizeActDraft = (draft = {}) => {
  const rows = Array.isArray(draft?.rows) && draft.rows.length
    ? draft.rows.map(normalizeRow)
    : [createEmptyRow()];

  return {
    act_number: String(draft?.act_number || ''),
    act_date: String(draft?.act_date || ''),
    supplier_name: String(draft?.supplier_name || ''),
    supplier_inn: String(draft?.supplier_inn || ''),
    supplier_kpp: String(draft?.supplier_kpp || ''),
    supplier_summary: String(draft?.supplier_summary || ''),
    buyer_name: String(draft?.buyer_name || ''),
    buyer_inn: String(draft?.buyer_inn || ''),
    buyer_kpp: String(draft?.buyer_kpp || ''),
    buyer_summary: String(draft?.buyer_summary || ''),
    vat_text: String(draft?.vat_text || DEFAULT_VAT_TEXT),
    contractor_label: String(draft?.contractor_label || 'Исполнитель'),
    contractor_name: String(draft?.contractor_name || ''),
    customer_label: String(draft?.customer_label || 'Заказчик'),
    customer_name: String(draft?.customer_name || ''),
    completion_note: String(draft?.completion_note || DEFAULT_ACT_COMPLETION_NOTE),
    amount_in_words: String(draft?.amount_in_words || ''),
    rows,
  };
};

const updateDraftWithDerived = (draft, updater) => {
  const previous = normalizeDraft(draft);
  const previousAutoWords = buildInvoiceAmountWords(previous.rows, previous.vat_text || DEFAULT_VAT_TEXT);
  const next = normalizeDraft(updater(previous));
  const nextAutoWords = buildInvoiceAmountWords(next.rows, next.vat_text || DEFAULT_VAT_TEXT);
  const currentAmountWords = String(next.amount_in_words || '').trim();
  const previousAmountWords = String(previous.amount_in_words || '').trim();

  if (!currentAmountWords || previousAmountWords === previousAutoWords) {
    next.amount_in_words = nextAutoWords;
  }

  return next;
};

const updateActDraftWithDerived = (draft, updater) => {
  const previous = normalizeActDraft(draft);
  const previousAutoWords = buildInvoiceAmountWords(previous.rows, previous.vat_text || DEFAULT_VAT_TEXT);
  const next = normalizeActDraft(updater(previous));
  const nextAutoWords = buildInvoiceAmountWords(next.rows, next.vat_text || DEFAULT_VAT_TEXT);
  const currentAmountWords = String(next.amount_in_words || '').trim();
  const previousAmountWords = String(previous.amount_in_words || '').trim();

  if (!currentAmountWords || previousAmountWords === previousAutoWords) {
    next.amount_in_words = nextAutoWords;
  }

  return next;
};

const fieldClassName =
  'w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 outline-none transition-colors focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-500 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100 dark:disabled:bg-gray-800 dark:disabled:text-gray-400';
const sectionTitleClassName = 'text-xs font-semibold uppercase tracking-[0.14em] text-gray-500 dark:text-gray-400';

const LabeledField = ({ label, children, className = '' }) => (
  <label className={`block space-y-1 ${className}`}>
    <div className={sectionTitleClassName}>{label}</div>
    {children}
  </label>
);

const DocumentAccordion = ({
  title,
  subtitle,
  badge,
  open,
  onToggle,
  actions,
  children,
}) => (
  <section className="overflow-hidden rounded-2xl sm:rounded-3xl border border-gray-200 bg-white shadow-xs dark:border-gray-800 dark:bg-gray-900/70">
    <button
      type="button"
      onClick={onToggle}
      className="flex w-full items-center justify-between gap-3 px-4 py-3.5 sm:px-5 sm:py-4 text-left"
    >
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">{title}</div>
          {badge}
        </div>
        {subtitle ? (
          <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">{subtitle}</div>
        ) : null}
      </div>
      <div className="flex items-center gap-2">
        {actions}
        <ChevronDown className={`h-4 w-4 text-gray-500 transition-transform ${open ? 'rotate-180' : ''}`} />
      </div>
    </button>
    {open ? <div className="border-t border-gray-100 px-4 py-4 sm:px-5 sm:py-5 dark:border-gray-800">{children}</div> : null}
  </section>
);

const formatPreparedMeta = (preparedAt, preparedByName) => {
  if (!preparedAt) return 'Ещё не сформирован';
  const date = new Date(preparedAt);
  const formattedDate = Number.isNaN(date.getTime())
    ? preparedAt
    : date.toLocaleString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
  return `Сформирован ${formattedDate}${preparedByName ? ` · ${preparedByName}` : ''}`;
};

const copyInvoiceToActDraft = (invoiceDraft, actDraft) => {
  const invoice = normalizeDraft(invoiceDraft);
  const currentAct = normalizeActDraft(actDraft);
  const rows = invoice.rows.map((row) => ({ ...row }));
  return normalizeActDraft({
    ...currentAct,
    supplier_name: invoice.supplier_name,
    supplier_inn: invoice.supplier_inn,
    supplier_kpp: invoice.supplier_kpp,
    supplier_summary: invoice.supplier_summary,
    buyer_name: invoice.buyer_name,
    buyer_inn: invoice.buyer_inn,
    buyer_kpp: invoice.buyer_kpp,
    buyer_summary: invoice.buyer_summary,
    vat_text: invoice.vat_text,
    contractor_name: currentAct.contractor_name || invoice.director_name,
    customer_name: currentAct.customer_name,
    rows,
    amount_in_words: buildInvoiceAmountWords(rows, invoice.vat_text || DEFAULT_VAT_TEXT),
  });
};

/**
 * Модальное окно выбора единицы измерения
 */
const UnitPickerModal = ({ open, onClose, currentUnit, onSelect }) => {
  const [customValue, setCustomValue] = useState(currentUnit || '');

  useEffect(() => {
    setCustomValue(currentUnit || '');
  }, [currentUnit, open]);

  if (!open) return null;

  return (
    <ModalShell
      open={open}
      onClose={onClose}
      variant="sheet"
      zIndexClass="z-[90]"
      panelClassName="p-4 sm:p-5 sm:max-w-md w-full"
    >
      <div className="space-y-4">
        <div className="flex items-center justify-between border-b border-gray-100 pb-3 dark:border-gray-800">
          <div className="text-base font-bold text-gray-900 dark:text-gray-100">
            Единица измерения
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1 text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Стандартные варианты */}
        <div>
          <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
            Стандартные единицы
          </label>
          <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
            {PRESET_UNITS.map((unit) => {
              const isSelected = customValue.trim().toLowerCase() === unit.value.toLowerCase();
              return (
                <button
                  key={unit.value}
                  type="button"
                  onClick={() => {
                    onSelect(unit.value);
                    onClose();
                  }}
                  className={`flex items-center justify-between rounded-xl px-3 py-2 text-left text-sm transition-all ${
                    isSelected
                      ? 'border border-blue-500 bg-blue-50 font-semibold text-blue-700 dark:border-blue-500/60 dark:bg-blue-950/40 dark:text-blue-300'
                      : 'border border-gray-200 bg-white hover:bg-gray-50 dark:border-gray-800 dark:bg-gray-900 dark:hover:bg-gray-800 text-gray-900 dark:text-gray-100'
                  }`}
                >
                  <div className="min-w-0">
                    <div className="font-medium">{unit.label}</div>
                    <div className="text-[11px] text-gray-500 dark:text-gray-400">{unit.desc}</div>
                  </div>
                  {isSelected && <Check className="h-4 w-4 shrink-0 text-blue-600 dark:text-blue-400" />}
                </button>
              );
            })}
          </div>
        </div>

        {/* Своё значение */}
        <div className="border-t border-gray-100 pt-3 dark:border-gray-800">
          <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
            Своё значение
          </label>
          <div className="flex gap-2">
            <input
              type="text"
              value={customValue}
              onChange={(e) => setCustomValue(e.target.value)}
              placeholder="Например, рул. или упаковка"
              className={fieldClassName}
            />
            <button
              type="button"
              onClick={() => {
                onSelect(customValue.trim() || 'усл.');
                onClose();
              }}
              className="inline-flex shrink-0 items-center rounded-xl bg-blue-600 px-4 py-2 text-xs font-semibold text-white shadow-xs hover:bg-blue-700 active:scale-95"
            >
              Применить
            </button>
          </div>
        </div>
      </div>
    </ModalShell>
  );
};

const InvoiceEditorModal = ({
  open,
  onClose,
  draft,
  actDraft,
  invoiceNumber,
  actNumber,
  preparedAt,
  preparedByName,
  actPreparedAt,
  actPreparedByName,
  loading = false,
  saving = false,
  canEdit = true,
  isCompletedOrder = false,
  orderFinalAmount = null,
  onSave,
  onDownload,
  onPrint,
  onDownloadAct,
  onPrintAct,
  onDownloadPackage,
  onPrintPackage,
  packageDownloading = false,
  packagePrinting = false,
  canDownload = false,
  canPrint = false,
  canDownloadAct = false,
  canPrintAct = false,
  canDownloadPackage = false,
  canPrintPackage = false,
}) => {
  const closeWithBack = useModalBackButton(open, onClose);
  const [localDraft, setLocalDraft] = useState(() => normalizeDraft(draft));
  const [localActDraft, setLocalActDraft] = useState(() => normalizeActDraft(actDraft));
  const [activeDocTab, setActiveDocTab] = useState('invoice'); // 'invoice' | 'act'
  const [unitPickerTarget, setUnitPickerTarget] = useState(null); // { docType: 'invoice' | 'act', index: number, currentUnit: string } | null
  const [accordionSections, setAccordionSections] = useState({
    invoiceSupplier: false,
    invoiceBuyer: false,
    invoiceSignatures: false,
    actSupplier: false,
    actBuyer: false,
    actSignatures: false,
  });

  const { showToast } = useToast();

  useEffect(() => {
    if (!open) return;
    setLocalDraft(
      normalizeDraft({
        ...(draft || {}),
        invoice_number: draft?.invoice_number ?? invoiceNumber ?? '',
      })
    );
    setLocalActDraft(
      normalizeActDraft({
        ...(actDraft || {}),
        act_number: actDraft?.act_number ?? actNumber ?? invoiceNumber ?? '',
      })
    );
  }, [draft, actDraft, invoiceNumber, actNumber, open]);

  const normalizedSourceDraft = useMemo(
    () =>
      normalizeDraft({
        ...(draft || {}),
        invoice_number: draft?.invoice_number ?? invoiceNumber ?? '',
      }),
    [draft, invoiceNumber]
  );

  const normalizedActSourceDraft = useMemo(
    () =>
      normalizeActDraft({
        ...(actDraft || {}),
        act_number: actDraft?.act_number ?? actNumber ?? invoiceNumber ?? '',
      }),
    [actDraft, actNumber, invoiceNumber]
  );

  const normalizedLocalDraft = useMemo(() => normalizeDraft(localDraft), [localDraft]);
  const normalizedLocalActDraft = useMemo(() => normalizeActDraft(localActDraft), [localActDraft]);

  const rowsTotal = useMemo(
    () => getInvoiceRowsTotal(normalizedLocalDraft.rows),
    [normalizedLocalDraft.rows]
  );
  const actRowsTotal = useMemo(
    () => getInvoiceRowsTotal(normalizedLocalActDraft.rows),
    [normalizedLocalActDraft.rows]
  );

  const recommendedAmountWords = useMemo(
    () => buildInvoiceAmountWords(normalizedLocalDraft.rows, normalizedLocalDraft.vat_text || DEFAULT_VAT_TEXT),
    [normalizedLocalDraft.rows, normalizedLocalDraft.vat_text]
  );
  const actRecommendedAmountWords = useMemo(
    () => buildInvoiceAmountWords(normalizedLocalActDraft.rows, normalizedLocalActDraft.vat_text || DEFAULT_VAT_TEXT),
    [normalizedLocalActDraft.rows, normalizedLocalActDraft.vat_text]
  );

  const expectedAmount = Number(orderFinalAmount) || 0;
  const isAmountValid = useMemo(() => {
    if (!isCompletedOrder) return true;
    const invDiff = Math.abs(rowsTotal - expectedAmount);
    const actDiff = Math.abs(actRowsTotal - expectedAmount);
    return invDiff < 0.01 && actDiff < 0.01;
  }, [isCompletedOrder, rowsTotal, actRowsTotal, expectedAmount]);

  const isDirty = useMemo(
    () =>
      JSON.stringify(normalizedLocalDraft) !== JSON.stringify(normalizedSourceDraft) ||
      JSON.stringify(normalizedLocalActDraft) !== JSON.stringify(normalizedActSourceDraft),
    [normalizedLocalDraft, normalizedSourceDraft, normalizedLocalActDraft, normalizedActSourceDraft]
  );

  const canSave = canEdit && !loading && !saving && isAmountValid && (isDirty || !preparedAt || !actPreparedAt);

  const setDraftField = (field, value) => {
    setLocalDraft((prev) =>
      updateDraftWithDerived(prev, (current) => ({
        ...current,
        [field]: value,
      }))
    );
  };

  const setActField = (field, value) => {
    setLocalActDraft((prev) =>
      updateActDraftWithDerived(prev, (current) => ({
        ...current,
        [field]: value,
      }))
    );
  };

  const setRowField = (rowIndex, field, value) => {
    setLocalDraft((prev) =>
      updateDraftWithDerived(prev, (current) => ({
        ...current,
        rows: current.rows.map((row, index) =>
          index === rowIndex ? { ...row, [field]: value } : row
        ),
      }))
    );
  };

  const setActRowField = (rowIndex, field, value) => {
    setLocalActDraft((prev) =>
      updateActDraftWithDerived(prev, (current) => ({
        ...current,
        rows: current.rows.map((row, index) =>
          index === rowIndex ? { ...row, [field]: value } : row
        ),
      }))
    );
  };

  const addRow = () => {
    if (isCompletedOrder) return;
    setLocalDraft((prev) =>
      updateDraftWithDerived(prev, (current) => ({
        ...current,
        rows: [...current.rows, createEmptyRow()],
      }))
    );
  };

  const removeRow = (rowIndex) => {
    if (isCompletedOrder) return;
    setLocalDraft((prev) =>
      updateDraftWithDerived(prev, (current) => ({
        ...current,
        rows:
          current.rows.length > 1
            ? current.rows.filter((_, index) => index !== rowIndex)
            : [createEmptyRow()],
      }))
    );
  };

  const addActRow = () => {
    if (isCompletedOrder) return;
    setLocalActDraft((prev) =>
      updateActDraftWithDerived(prev, (current) => ({
        ...current,
        rows: [...current.rows, createEmptyRow()],
      }))
    );
  };

  const removeActRow = (rowIndex) => {
    if (isCompletedOrder) return;
    setLocalActDraft((prev) =>
      updateActDraftWithDerived(prev, (current) => ({
        ...current,
        rows:
          current.rows.length > 1
            ? current.rows.filter((_, index) => index !== rowIndex)
            : [createEmptyRow()],
      }))
    );
  };

  const resetAmountInWords = () => {
    setLocalDraft((prev) => ({
      ...normalizeDraft(prev),
      amount_in_words: recommendedAmountWords,
    }));
  };

  const resetActAmountInWords = () => {
    setLocalActDraft((prev) => ({
      ...normalizeActDraft(prev),
      amount_in_words: actRecommendedAmountWords,
    }));
  };

  const syncActWithInvoice = () => {
    setLocalActDraft((prev) => copyInvoiceToActDraft(normalizedLocalDraft, prev));
    showToast('success', 'Данные акта синхронизированы со счётом');
  };

  const toggleAccordion = (key) => {
    setAccordionSections((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleUnitSelected = (selectedUnit) => {
    if (!unitPickerTarget) return;
    const { docType, index } = unitPickerTarget;
    if (docType === 'invoice') {
      setRowField(index, 'unit', selectedUnit);
    } else {
      setActRowField(index, 'unit', selectedUnit);
    }
  };

  const handleSave = () => {
    if (!canSave) return;
    if (isCompletedOrder && !isAmountValid) {
      showToast(
        'error',
        `Сумма счёта (${formatInvoiceMoney(rowsTotal)} ₽) должна совпадать с суммой закрытого заказа (${formatInvoiceMoney(expectedAmount)} ₽)`
      );
      return;
    }
    onSave?.({
      invoice: normalizedLocalDraft,
      act: normalizedLocalActDraft,
    });
  };

  const showCompletedOrderInfoToast = () => {
    showToast(
      'info',
      `В закрытых заказах редактирование реквизитов и формулировок разрешено, но итоговая сумма должна совпадать с суммой закрытого заказа (${formatInvoiceMoney(expectedAmount)} ₽). Версия документов до закрытия сохраняется во вложениях.`
    );
  };

  return (
    <>
      <ModalShell
        open={open}
        onClose={onClose}
        variant="page"
        zIndexClass="z-[75]"
        closeOnOverlay={false}
        panelClassName="h-full w-full max-w-full overflow-hidden flex flex-col"
      >
        <div className="flex h-full w-full max-w-full overflow-hidden flex-col bg-slate-100 dark:bg-slate-950">
          {/* Sticky Header */}
          <header className="sticky top-0 z-20 select-none border-b border-gray-200 bg-white/95 shadow-xs backdrop-blur-sm dark:border-gray-800 dark:bg-gray-900/95">
            <div className="mx-auto flex w-full max-w-7xl items-center justify-between px-3 py-2 sm:px-6 sm:py-2.5">
              <div className="flex min-w-0 items-center gap-1.5 sm:gap-2">
                <button
                  type="button"
                  onClick={closeWithBack}
                  className="rounded-xl p-1.5 sm:p-2 transition-colors hover:bg-gray-100 dark:hover:bg-gray-800"
                  aria-label="Вернуться к заказу"
                >
                  <ArrowLeft className="h-5 w-5 text-gray-700 dark:text-gray-200" />
                </button>
                <div className="truncate text-sm sm:text-base font-bold text-gray-900 dark:text-gray-100">
                  Документы юрлица
                </div>
              </div>

              <div className="flex items-center gap-1.5 sm:gap-2">
                <button
                  type="button"
                  onClick={onDownloadPackage}
                  disabled={!canDownloadPackage}
                  title="Скачать PDF-пакет"
                  className="inline-flex h-8 sm:h-9 items-center gap-1 sm:gap-1.5 rounded-xl border border-gray-300 px-2 sm:px-2.5 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-50 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800"
                >
                  <Download className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                  <span className="hidden sm:inline">
                    {packageDownloading ? 'Готовим...' : 'Пакет'}
                  </span>
                </button>
                <button
                  type="button"
                  onClick={onPrintPackage}
                  disabled={!canPrintPackage}
                  title="Печать PDF-пакета"
                  className="inline-flex h-8 sm:h-9 items-center gap-1 sm:gap-1.5 rounded-xl border border-gray-300 px-2 sm:px-2.5 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-50 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800"
                >
                  <Printer className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                  <span className="hidden sm:inline">
                    {packagePrinting ? 'Открываем...' : 'Печать'}
                  </span>
                </button>
              </div>
            </div>
          </header>

          {/* Scrollable Content */}
          <div className="flex-1 w-full max-w-full overflow-y-auto overflow-x-hidden px-3 py-3 sm:px-6 sm:py-5">
            <div className="mx-auto w-full max-w-7xl space-y-3 sm:space-y-4">
              {loading ? (
                <div className="flex h-56 items-center justify-center rounded-2xl border border-dashed border-gray-300 bg-white text-sm text-gray-500 dark:border-gray-700 dark:bg-gray-900/50 dark:text-gray-400">
                  Загружаю документы...
                </div>
              ) : (
                <>
                  {/* Компактная плашка для закрытых заказов с кнопкой инфо */}
                  {isCompletedOrder && (
                    <div className="flex items-center justify-between gap-2 rounded-xl border border-amber-200 bg-amber-50/90 px-2.5 py-1.5 text-xs text-amber-900 shadow-2xs dark:border-amber-800/60 dark:bg-amber-950/40 dark:text-amber-200">
                      <div className="flex min-w-0 items-center gap-1.5 truncate font-medium">
                        <Lock className="h-3.5 w-3.5 shrink-0 text-amber-600 dark:text-amber-400" />
                        <span className="truncate">
                          Заказ закрыт · Сумма {formatInvoiceMoney(expectedAmount)} ₽
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={showCompletedOrderInfoToast}
                        className="inline-flex shrink-0 items-center gap-1 rounded-lg bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-900 transition-colors hover:bg-amber-200 active:scale-95 dark:bg-amber-900/50 dark:text-amber-100"
                      >
                        <Info className="h-3 w-3 text-amber-600 dark:text-amber-400" />
                        <span>Инфо</span>
                      </button>
                    </div>
                  )}

                  {/* Переключатель вкладок и действия */}
                  <div className="space-y-2 border-b border-gray-200 pb-2.5 dark:border-gray-800">
                    <div className="grid grid-cols-2 gap-1 rounded-xl bg-gray-200/80 p-1 dark:bg-gray-800">
                      <button
                        type="button"
                        onClick={() => setActiveDocTab('invoice')}
                        className={`flex items-center justify-center gap-1.5 rounded-lg py-1.5 text-xs font-semibold transition-all ${
                          activeDocTab === 'invoice'
                            ? 'bg-white text-blue-600 shadow-xs dark:bg-gray-900 dark:text-blue-400'
                            : 'text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200'
                        }`}
                      >
                        <FileText className="h-3.5 w-3.5 shrink-0" />
                        <span className="truncate">Счёт на оплату</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => setActiveDocTab('act')}
                        className={`flex items-center justify-center gap-1.5 rounded-lg py-1.5 text-xs font-semibold transition-all ${
                          activeDocTab === 'act'
                            ? 'bg-white text-blue-600 shadow-xs dark:bg-gray-900 dark:text-blue-400'
                            : 'text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200'
                        }`}
                      >
                        <FileCheck className="h-3.5 w-3.5 shrink-0" />
                        <span className="truncate">Акт выполненных работ</span>
                      </button>
                    </div>

                    <div className="flex flex-wrap items-center justify-between gap-1.5 pt-0.5">
                      {activeDocTab === 'act' ? (
                        <button
                          type="button"
                          onClick={syncActWithInvoice}
                          disabled={!canEdit}
                          title="Скопировать услуги и реквизиты из счёта"
                          className="inline-flex h-8 items-center gap-1 rounded-lg border border-blue-200 bg-blue-50 px-2 text-xs font-medium text-blue-700 transition-colors hover:bg-blue-100 disabled:opacity-50 dark:border-blue-900 dark:bg-blue-950/30 dark:text-blue-300"
                        >
                          <Copy className="h-3.5 w-3.5 shrink-0" />
                          <span>Скопировать из счёта</span>
                        </button>
                      ) : (
                        <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                          {formatPreparedMeta(preparedAt, preparedByName)}
                        </div>
                      )}

                      <div className="flex items-center gap-1.5 ml-auto">
                        <button
                          type="button"
                          onClick={activeDocTab === 'act' ? onDownloadAct : onDownload}
                          disabled={activeDocTab === 'act' ? !canDownloadAct : !canDownload}
                          title={activeDocTab === 'act' ? 'Скачать PDF акта' : 'Скачать PDF счёта'}
                          className="inline-flex h-8 items-center gap-1 rounded-lg border border-gray-300 px-2 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-50 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800"
                        >
                          <Download className="h-3.5 w-3.5 shrink-0" />
                          <span>Скачать</span>
                        </button>
                        <button
                          type="button"
                          onClick={activeDocTab === 'act' ? onPrintAct : onPrint}
                          disabled={activeDocTab === 'act' ? !canPrintAct : !canPrint}
                          title={activeDocTab === 'act' ? 'Печать акта' : 'Печать счёта'}
                          className="inline-flex h-8 items-center gap-1 rounded-lg border border-gray-300 px-2 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-50 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800"
                        >
                          <Printer className="h-3.5 w-3.5 shrink-0" />
                          <span>Печать</span>
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Вкладка 1: СЧЁТ НА ОПЛАТУ */}
                  {activeDocTab === 'invoice' && (
                    <div className="space-y-3 sm:space-y-4">
                      <div className="grid gap-3 sm:gap-4 lg:grid-cols-[minmax(0,1.3fr)_minmax(0,0.7fr)]">
                        {/* Левая колонка: Параметры и Строки */}
                        <div className="space-y-3 sm:space-y-4 min-w-0">
                          {/* Параметры счёта */}
                          <section className="space-y-3 rounded-2xl border border-gray-200 bg-white p-3.5 shadow-xs dark:border-gray-800 dark:bg-gray-900/70 sm:p-4">
                            <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                              Параметры счёта
                            </div>
                            <div className="grid gap-2.5 sm:grid-cols-3">
                              <LabeledField label="Номер счёта">
                                <input
                                  type="text"
                                  value={normalizedLocalDraft.invoice_number}
                                  onChange={(event) => setDraftField('invoice_number', event.target.value)}
                                  disabled={!canEdit}
                                  className={fieldClassName}
                                  placeholder="Например, 2"
                                />
                              </LabeledField>
                              <LabeledField label="Дата счёта">
                                <input
                                  type="date"
                                  value={normalizedLocalDraft.invoice_date}
                                  onChange={(event) => setDraftField('invoice_date', event.target.value)}
                                  disabled={!canEdit}
                                  className={fieldClassName}
                                />
                              </LabeledField>
                              <LabeledField label="Текст НДС">
                                <input
                                  type="text"
                                  value={normalizedLocalDraft.vat_text}
                                  onChange={(event) => setDraftField('vat_text', event.target.value)}
                                  disabled={!canEdit}
                                  className={fieldClassName}
                                  placeholder={DEFAULT_VAT_TEXT}
                                />
                              </LabeledField>
                            </div>
                            <LabeledField label="Верхнее примечание">
                              <AutoResizeTextarea
                                value={normalizedLocalDraft.header_note}
                                onChange={(event) => setDraftField('header_note', event.target.value)}
                                disabled={!canEdit}
                                minHeight={68}
                                maxHeight={190}
                                className={`${fieldClassName} text-xs leading-relaxed`}
                                placeholder="Примечание к счёту (необязательно)"
                              />
                            </LabeledField>
                          </section>

                          {/* Таблица услуг */}
                          <section className="space-y-3 rounded-2xl border border-gray-200 bg-white p-3.5 shadow-xs dark:border-gray-800 dark:bg-gray-900/70 sm:p-4">
                            <div className="flex items-center justify-between">
                              <div>
                                <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                                  Услуги в счёте
                                </div>
                                <div className="text-xs text-gray-500 dark:text-gray-400">
                                  {isCompletedOrder
                                    ? 'Сумма зафиксирована по заказу'
                                    : 'Наименование, единицы измерения и стоимость'}
                                </div>
                              </div>
                              {!isCompletedOrder && canEdit && (
                                <button
                                  type="button"
                                  onClick={addRow}
                                  className="inline-flex items-center gap-1 rounded-xl border border-blue-200 bg-blue-50 px-2.5 py-1.5 text-xs font-medium text-blue-700 transition-colors hover:bg-blue-100 dark:border-blue-900 dark:bg-blue-950/30 dark:text-blue-300"
                                >
                                  <Plus className="h-3.5 w-3.5" />
                                  <span>Добавить строку</span>
                                </button>
                              )}
                            </div>

                            <div className="space-y-3">
                              {normalizedLocalDraft.rows.map((row, index) => {
                                const quantity = parseInvoiceDecimal(row.quantity, 1);
                                const price = parseInvoiceDecimal(row.price, 0);
                                const rowAmount = formatInvoiceMoney(quantity * price);

                                return (
                                  <div
                                    key={index}
                                    className="space-y-2.5 rounded-xl border border-gray-200 bg-gray-50/70 p-3 dark:border-gray-800 dark:bg-gray-950/40"
                                  >
                                    <div className="flex items-center justify-between gap-2">
                                      <span className="text-xs font-semibold text-gray-500">
                                        Позиция {index + 1}
                                      </span>
                                      {!isCompletedOrder && canEdit && normalizedLocalDraft.rows.length > 1 && (
                                        <button
                                          type="button"
                                          onClick={() => removeRow(index)}
                                          className="inline-flex items-center gap-1 rounded-lg px-2 py-0.5 text-xs text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950/30"
                                        >
                                          <Trash2 className="h-3.5 w-3.5" />
                                          <span>Удалить</span>
                                        </button>
                                      )}
                                    </div>

                                    <LabeledField label="Наименование услуги">
                                      <input
                                        type="text"
                                        value={row.name}
                                        onChange={(event) => setRowField(index, 'name', event.target.value)}
                                        disabled={!canEdit}
                                        className={fieldClassName}
                                        placeholder="Например, Прачечные услуги по договору"
                                      />
                                    </LabeledField>

                                    <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 sm:gap-3">
                                      <LabeledField label="Кол-во">
                                        <input
                                          type="text"
                                          value={row.quantity}
                                          onChange={(event) => setRowField(index, 'quantity', event.target.value)}
                                          disabled={!canEdit}
                                          className={fieldClassName}
                                        />
                                      </LabeledField>

                                      <LabeledField label="Ед. изм.">
                                        <div className="relative">
                                          <input
                                            type="text"
                                            value={row.unit}
                                            onChange={(event) => setRowField(index, 'unit', event.target.value)}
                                            disabled={!canEdit}
                                            className={`${fieldClassName} pr-9`}
                                            placeholder="усл."
                                          />
                                          {canEdit && (
                                            <button
                                              type="button"
                                              onClick={() =>
                                                setUnitPickerTarget({
                                                  docType: 'invoice',
                                                  index,
                                                  currentUnit: row.unit,
                                                })
                                              }
                                              title="Выбрать единицу измерения из списка"
                                              className="absolute right-1 top-1/2 -translate-y-1/2 rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-blue-600 dark:hover:bg-gray-800 dark:hover:text-blue-400"
                                            >
                                              <SlidersHorizontal className="h-4 w-4" />
                                            </button>
                                          )}
                                        </div>
                                      </LabeledField>

                                      <LabeledField label={isCompletedOrder ? 'Цена (фикс.)' : 'Цена'}>
                                        <div>
                                          <input
                                            type="text"
                                            value={row.price}
                                            onChange={(event) => setRowField(index, 'price', event.target.value)}
                                            disabled={!canEdit || isCompletedOrder}
                                            className={fieldClassName}
                                          />
                                          {isCompletedOrder && (
                                            <div className="mt-1 flex items-center gap-1 text-[10px] text-gray-500">
                                              <Lock className="h-2.5 w-2.5" />
                                              <span>Зафиксирована</span>
                                            </div>
                                          )}
                                        </div>
                                      </LabeledField>

                                      <div className="space-y-1">
                                        <div className={sectionTitleClassName}>Сумма</div>
                                        <div className="flex min-h-[38px] sm:min-h-[42px] items-center justify-end rounded-xl border border-gray-200 bg-white px-3 text-sm font-semibold text-gray-900 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100">
                                          {rowAmount} ₽
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </section>
                        </div>

                        {/* Правая колонка: Итоги и Реквизиты */}
                        <div className="space-y-3 sm:space-y-4 min-w-0">
                          <section className="space-y-3 rounded-2xl border border-gray-200 bg-white p-3.5 shadow-xs dark:border-gray-800 dark:bg-gray-900/70 sm:p-4">
                            <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">Итоги счёта</div>
                            <div className="rounded-xl border border-blue-200 bg-blue-50 px-3.5 py-3 dark:border-blue-900 dark:bg-blue-950/20">
                              <div className="text-xs font-semibold uppercase tracking-wider text-blue-700 dark:text-blue-300">
                                Итого к оплате
                              </div>
                              <div className="mt-1 text-2xl font-bold text-blue-700 dark:text-blue-200">
                                {formatInvoiceMoney(rowsTotal)} руб.
                              </div>
                              <div className="mt-0.5 text-xs text-blue-600/80 dark:text-blue-300/80">
                                {normalizedLocalDraft.vat_text}
                              </div>
                            </div>

                            <div className="space-y-1.5">
                              <div className="flex items-center justify-between">
                                <span className={sectionTitleClassName}>Сумма прописью</span>
                                {canEdit && (
                                  <button
                                    type="button"
                                    onClick={resetAmountInWords}
                                    className="inline-flex items-center gap-1 text-[11px] font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400"
                                  >
                                    <RefreshCw className="h-3 w-3" />
                                    <span>Сгенерировать</span>
                                  </button>
                                )}
                              </div>
                              <AutoResizeTextarea
                                value={normalizedLocalDraft.amount_in_words}
                                onChange={(event) => setDraftField('amount_in_words', event.target.value)}
                                disabled={!canEdit}
                                minHeight={42}
                                maxHeight={120}
                                className={`${fieldClassName} text-xs leading-relaxed`}
                                placeholder="Сумма прописью"
                              />
                            </div>
                          </section>

                          {/* Аккордеон: Реквизиты исполнителя */}
                          <DocumentAccordion
                            title="Реквизиты исполнителя"
                            subtitle={normalizedLocalDraft.supplier_name || 'Не заполнено'}
                            open={accordionSections.invoiceSupplier}
                            onToggle={() => toggleAccordion('invoiceSupplier')}
                          >
                            <div className="space-y-3">
                              <LabeledField label="Исполнитель (кратко)">
                                <input
                                  type="text"
                                  value={normalizedLocalDraft.supplier_name}
                                  onChange={(event) => setDraftField('supplier_name', event.target.value)}
                                  disabled={!canEdit}
                                  className={fieldClassName}
                                />
                              </LabeledField>
                              <div className="grid grid-cols-2 gap-2">
                                <LabeledField label="ИНН">
                                  <input
                                    type="text"
                                    value={normalizedLocalDraft.supplier_inn}
                                    onChange={(event) => setDraftField('supplier_inn', event.target.value)}
                                    disabled={!canEdit}
                                    className={fieldClassName}
                                  />
                                </LabeledField>
                                <LabeledField label="КПП">
                                  <input
                                    type="text"
                                    value={normalizedLocalDraft.supplier_kpp}
                                    onChange={(event) => setDraftField('supplier_kpp', event.target.value)}
                                    disabled={!canEdit}
                                    className={fieldClassName}
                                  />
                                </LabeledField>
                              </div>
                              <LabeledField label="Банк">
                                <input
                                  type="text"
                                  value={normalizedLocalDraft.bank_name}
                                  onChange={(event) => setDraftField('bank_name', event.target.value)}
                                  disabled={!canEdit}
                                  className={fieldClassName}
                                />
                              </LabeledField>
                              <div className="grid grid-cols-2 gap-2">
                                <LabeledField label="БИК">
                                  <input
                                    type="text"
                                    value={normalizedLocalDraft.bik}
                                    onChange={(event) => setDraftField('bik', event.target.value)}
                                    disabled={!canEdit}
                                    className={fieldClassName}
                                  />
                                </LabeledField>
                                <LabeledField label="Счёт №">
                                  <input
                                    type="text"
                                    value={normalizedLocalDraft.account}
                                    onChange={(event) => setDraftField('account', event.target.value)}
                                    disabled={!canEdit}
                                    className={fieldClassName}
                                  />
                                </LabeledField>
                              </div>
                              <LabeledField label="Корр. счёт">
                                <input
                                  type="text"
                                  value={normalizedLocalDraft.corr_account}
                                  onChange={(event) => setDraftField('corr_account', event.target.value)}
                                  disabled={!canEdit}
                                  className={fieldClassName}
                                />
                              </LabeledField>
                              <LabeledField label="Полное наименование и адрес">
                                <AutoResizeTextarea
                                  value={normalizedLocalDraft.supplier_summary}
                                  onChange={(event) => setDraftField('supplier_summary', event.target.value)}
                                  disabled={!canEdit}
                                  minHeight={52}
                                  maxHeight={160}
                                  className={`${fieldClassName} text-xs leading-relaxed`}
                                  placeholder="Полное наименование организации, юр. адрес"
                                />
                              </LabeledField>
                            </div>
                          </DocumentAccordion>

                          {/* Аккордеон: Реквизиты заказчика */}
                          <DocumentAccordion
                            title="Реквизиты заказчика"
                            subtitle={normalizedLocalDraft.buyer_name || 'Не заполнено'}
                            open={accordionSections.invoiceBuyer}
                            onToggle={() => toggleAccordion('invoiceBuyer')}
                          >
                            <div className="space-y-3">
                              <LabeledField label="Заказчик (кратко)">
                                <input
                                  type="text"
                                  value={normalizedLocalDraft.buyer_name}
                                  onChange={(event) => setDraftField('buyer_name', event.target.value)}
                                  disabled={!canEdit}
                                  className={fieldClassName}
                                />
                              </LabeledField>
                              <div className="grid grid-cols-2 gap-2">
                                <LabeledField label="ИНН">
                                  <input
                                    type="text"
                                    value={normalizedLocalDraft.buyer_inn}
                                    onChange={(event) => setDraftField('buyer_inn', event.target.value)}
                                    disabled={!canEdit}
                                    className={fieldClassName}
                                  />
                                </LabeledField>
                                <LabeledField label="КПП">
                                  <input
                                    type="text"
                                    value={normalizedLocalDraft.buyer_kpp}
                                    onChange={(event) => setDraftField('buyer_kpp', event.target.value)}
                                    disabled={!canEdit}
                                    className={fieldClassName}
                                  />
                                </LabeledField>
                              </div>
                              <LabeledField label="Полное наименование и адрес">
                                <AutoResizeTextarea
                                  value={normalizedLocalDraft.buyer_summary}
                                  onChange={(event) => setDraftField('buyer_summary', event.target.value)}
                                  disabled={!canEdit}
                                  minHeight={52}
                                  maxHeight={160}
                                  className={`${fieldClassName} text-xs leading-relaxed`}
                                  placeholder="Полное наименование организации, юр. адрес"
                                />
                              </LabeledField>
                            </div>
                          </DocumentAccordion>

                          {/* Аккордеон: Подписи в счёте */}
                          <DocumentAccordion
                            title="Подписи в счёте"
                            subtitle={`${normalizedLocalDraft.director_name || '—'} / ${normalizedLocalDraft.accountant_name || '—'}`}
                            open={accordionSections.invoiceSignatures}
                            onToggle={() => toggleAccordion('invoiceSignatures')}
                          >
                            <div className="space-y-3">
                              <div className="grid grid-cols-2 gap-2">
                                <LabeledField label="Должность руководителя">
                                  <input
                                    type="text"
                                    value={normalizedLocalDraft.director_label}
                                    onChange={(event) => setDraftField('director_label', event.target.value)}
                                    disabled={!canEdit}
                                    className={fieldClassName}
                                  />
                                </LabeledField>
                                <LabeledField label="ФИО руководителя">
                                  <input
                                    type="text"
                                    value={normalizedLocalDraft.director_name}
                                    onChange={(event) => setDraftField('director_name', event.target.value)}
                                    disabled={!canEdit}
                                    className={fieldClassName}
                                  />
                                </LabeledField>
                              </div>
                              <div className="grid grid-cols-2 gap-2 pt-1">
                                <LabeledField label="Должность бухгалтера">
                                  <input
                                    type="text"
                                    value={normalizedLocalDraft.accountant_label}
                                    onChange={(event) => setDraftField('accountant_label', event.target.value)}
                                    disabled={!canEdit}
                                    className={fieldClassName}
                                  />
                                </LabeledField>
                                <LabeledField label="ФИО бухгалтера">
                                  <input
                                    type="text"
                                    value={normalizedLocalDraft.accountant_name}
                                    onChange={(event) => setDraftField('accountant_name', event.target.value)}
                                    disabled={!canEdit}
                                    className={fieldClassName}
                                  />
                                </LabeledField>
                              </div>
                            </div>
                          </DocumentAccordion>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Вкладка 2: АКТ ВЫПОЛНЕННЫХ РАБОТ */}
                  {activeDocTab === 'act' && (
                    <div className="space-y-3 sm:space-y-4">
                      <div className="grid gap-3 sm:gap-4 lg:grid-cols-[minmax(0,1.3fr)_minmax(0,0.7fr)]">
                        {/* Левая колонка: Параметры и Строки */}
                        <div className="space-y-3 sm:space-y-4 min-w-0">
                          {/* Параметры акта */}
                          <section className="space-y-3 rounded-2xl border border-gray-200 bg-white p-3.5 shadow-xs dark:border-gray-800 dark:bg-gray-900/70 sm:p-4">
                            <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                              Параметры акта
                            </div>
                            <div className="grid gap-2.5 sm:grid-cols-3">
                              <LabeledField label="Номер акта">
                                <input
                                  type="text"
                                  value={normalizedLocalActDraft.act_number}
                                  onChange={(event) => setActField('act_number', event.target.value)}
                                  disabled={!canEdit}
                                  className={fieldClassName}
                                  placeholder="Например, 2"
                                />
                              </LabeledField>
                              <LabeledField label="Дата акта">
                                <input
                                  type="date"
                                  value={normalizedLocalActDraft.act_date}
                                  onChange={(event) => setActField('act_date', event.target.value)}
                                  disabled={!canEdit}
                                  className={fieldClassName}
                                />
                              </LabeledField>
                              <LabeledField label="Текст НДС">
                                <input
                                  type="text"
                                  value={normalizedLocalActDraft.vat_text}
                                  onChange={(event) => setActField('vat_text', event.target.value)}
                                  disabled={!canEdit}
                                  className={fieldClassName}
                                  placeholder={DEFAULT_VAT_TEXT}
                                />
                              </LabeledField>
                            </div>
                            <LabeledField label="Текст подтверждения выполнения">
                              <AutoResizeTextarea
                                value={normalizedLocalActDraft.completion_note}
                                onChange={(event) => setActField('completion_note', event.target.value)}
                                disabled={!canEdit}
                                minHeight={68}
                                maxHeight={190}
                                className={`${fieldClassName} text-xs leading-relaxed`}
                                placeholder="Текст подтверждения оказания услуг"
                              />
                            </LabeledField>
                          </section>

                          {/* Таблица услуг акта */}
                          <section className="space-y-3 rounded-2xl border border-gray-200 bg-white p-3.5 shadow-xs dark:border-gray-800 dark:bg-gray-900/70 sm:p-4">
                            <div className="flex items-center justify-between">
                              <div>
                                <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                                  Услуги в акте
                                </div>
                                <div className="text-xs text-gray-500 dark:text-gray-400">
                                  {isCompletedOrder
                                    ? 'Сумма зафиксирована по заказу'
                                    : 'Наименование, единицы измерения и стоимость'}
                                </div>
                              </div>
                              {!isCompletedOrder && canEdit && (
                                <button
                                  type="button"
                                  onClick={addActRow}
                                  className="inline-flex items-center gap-1 rounded-xl border border-blue-200 bg-blue-50 px-2.5 py-1.5 text-xs font-medium text-blue-700 transition-colors hover:bg-blue-100 dark:border-blue-900 dark:bg-blue-950/30 dark:text-blue-300"
                                >
                                  <Plus className="h-3.5 w-3.5" />
                                  <span>Добавить строку</span>
                                </button>
                              )}
                            </div>

                            <div className="space-y-3">
                              {normalizedLocalActDraft.rows.map((row, index) => {
                                const quantity = parseInvoiceDecimal(row.quantity, 1);
                                const price = parseInvoiceDecimal(row.price, 0);
                                const rowAmount = formatInvoiceMoney(quantity * price);

                                return (
                                  <div
                                    key={index}
                                    className="space-y-2.5 rounded-xl border border-gray-200 bg-gray-50/70 p-3 dark:border-gray-800 dark:bg-gray-950/40"
                                  >
                                    <div className="flex items-center justify-between gap-2">
                                      <span className="text-xs font-semibold text-gray-500">
                                        Позиция {index + 1}
                                      </span>
                                      {!isCompletedOrder && canEdit && normalizedLocalActDraft.rows.length > 1 && (
                                        <button
                                          type="button"
                                          onClick={() => removeActRow(index)}
                                          className="inline-flex items-center gap-1 rounded-lg px-2 py-0.5 text-xs text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950/30"
                                        >
                                          <Trash2 className="h-3.5 w-3.5" />
                                          <span>Удалить</span>
                                        </button>
                                      )}
                                    </div>

                                    <LabeledField label="Наименование услуги">
                                      <input
                                        type="text"
                                        value={row.name}
                                        onChange={(event) => setActRowField(index, 'name', event.target.value)}
                                        disabled={!canEdit}
                                        className={fieldClassName}
                                        placeholder="Например, Прачечные услуги по договору"
                                      />
                                    </LabeledField>

                                    <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 sm:gap-3">
                                      <LabeledField label="Кол-во">
                                        <input
                                          type="text"
                                          value={row.quantity}
                                          onChange={(event) => setActRowField(index, 'quantity', event.target.value)}
                                          disabled={!canEdit}
                                          className={fieldClassName}
                                        />
                                      </LabeledField>

                                      <LabeledField label="Ед. изм.">
                                        <div className="relative">
                                          <input
                                            type="text"
                                            value={row.unit}
                                            onChange={(event) => setActRowField(index, 'unit', event.target.value)}
                                            disabled={!canEdit}
                                            className={`${fieldClassName} pr-9`}
                                            placeholder="усл."
                                          />
                                          {canEdit && (
                                            <button
                                              type="button"
                                              onClick={() =>
                                                setUnitPickerTarget({
                                                  docType: 'act',
                                                  index,
                                                  currentUnit: row.unit,
                                                })
                                              }
                                              title="Выбрать единицу измерения из списка"
                                              className="absolute right-1 top-1/2 -translate-y-1/2 rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-blue-600 dark:hover:bg-gray-800 dark:hover:text-blue-400"
                                            >
                                              <SlidersHorizontal className="h-4 w-4" />
                                            </button>
                                          )}
                                        </div>
                                      </LabeledField>

                                      <LabeledField label={isCompletedOrder ? 'Цена (фикс.)' : 'Цена'}>
                                        <div>
                                          <input
                                            type="text"
                                            value={row.price}
                                            onChange={(event) => setActRowField(index, 'price', event.target.value)}
                                            disabled={!canEdit || isCompletedOrder}
                                            className={fieldClassName}
                                          />
                                          {isCompletedOrder && (
                                            <div className="mt-1 flex items-center gap-1 text-[10px] text-gray-500">
                                              <Lock className="h-2.5 w-2.5" />
                                              <span>Зафиксирована</span>
                                            </div>
                                          )}
                                        </div>
                                      </LabeledField>

                                      <div className="space-y-1">
                                        <div className={sectionTitleClassName}>Сумма</div>
                                        <div className="flex min-h-[38px] sm:min-h-[42px] items-center justify-end rounded-xl border border-gray-200 bg-white px-3 text-sm font-semibold text-gray-900 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100">
                                          {rowAmount} ₽
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </section>
                        </div>

                        {/* Правая колонка: Итоги и Реквизиты */}
                        <div className="space-y-3 sm:space-y-4 min-w-0">
                          <section className="space-y-3 rounded-2xl border border-gray-200 bg-white p-3.5 shadow-xs dark:border-gray-800 dark:bg-gray-900/70 sm:p-4">
                            <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">Итоги акта</div>
                            <div className="rounded-xl border border-blue-200 bg-blue-50 px-3.5 py-3 dark:border-blue-900 dark:bg-blue-950/20">
                              <div className="text-xs font-semibold uppercase tracking-wider text-blue-700 dark:text-blue-300">
                                Итого по акту
                              </div>
                              <div className="mt-1 text-2xl font-bold text-blue-700 dark:text-blue-200">
                                {formatInvoiceMoney(actRowsTotal)} руб.
                              </div>
                              <div className="mt-0.5 text-xs text-blue-600/80 dark:text-blue-300/80">
                                {normalizedLocalActDraft.vat_text}
                              </div>
                            </div>

                            <div className="space-y-1.5">
                              <div className="flex items-center justify-between">
                                <span className={sectionTitleClassName}>Сумма прописью</span>
                                {canEdit && (
                                  <button
                                    type="button"
                                    onClick={resetActAmountInWords}
                                    className="inline-flex items-center gap-1 text-[11px] font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400"
                                  >
                                    <RefreshCw className="h-3 w-3" />
                                    <span>Сгенерировать</span>
                                  </button>
                                )}
                              </div>
                              <AutoResizeTextarea
                                value={normalizedLocalActDraft.amount_in_words}
                                onChange={(event) => setActField('amount_in_words', event.target.value)}
                                disabled={!canEdit}
                                minHeight={42}
                                maxHeight={120}
                                className={`${fieldClassName} text-xs leading-relaxed`}
                                placeholder="Сумма прописью"
                              />
                            </div>
                          </section>

                          {/* Аккордеон: Реквизиты исполнителя */}
                          <DocumentAccordion
                            title="Реквизиты исполнителя"
                            subtitle={normalizedLocalActDraft.supplier_name || 'Не заполнено'}
                            open={accordionSections.actSupplier}
                            onToggle={() => toggleAccordion('actSupplier')}
                          >
                            <div className="space-y-3">
                              <LabeledField label="Исполнитель (кратко)">
                                <input
                                  type="text"
                                  value={normalizedLocalActDraft.supplier_name}
                                  onChange={(event) => setActField('supplier_name', event.target.value)}
                                  disabled={!canEdit}
                                  className={fieldClassName}
                                />
                              </LabeledField>
                              <div className="grid grid-cols-2 gap-2">
                                <LabeledField label="ИНН">
                                  <input
                                    type="text"
                                    value={normalizedLocalActDraft.supplier_inn}
                                    onChange={(event) => setActField('supplier_inn', event.target.value)}
                                    disabled={!canEdit}
                                    className={fieldClassName}
                                  />
                                </LabeledField>
                                <LabeledField label="КПП">
                                  <input
                                    type="text"
                                    value={normalizedLocalActDraft.supplier_kpp}
                                    onChange={(event) => setActField('supplier_kpp', event.target.value)}
                                    disabled={!canEdit}
                                    className={fieldClassName}
                                  />
                                </LabeledField>
                              </div>
                              <LabeledField label="Полное наименование и адрес">
                                <AutoResizeTextarea
                                  value={normalizedLocalActDraft.supplier_summary}
                                  onChange={(event) => setActField('supplier_summary', event.target.value)}
                                  disabled={!canEdit}
                                  minHeight={52}
                                  maxHeight={160}
                                  className={`${fieldClassName} text-xs leading-relaxed`}
                                  placeholder="Полное наименование организации, юр. адрес"
                                />
                              </LabeledField>
                            </div>
                          </DocumentAccordion>

                          {/* Аккордеон: Реквизиты заказчика */}
                          <DocumentAccordion
                            title="Реквизиты заказчика"
                            subtitle={normalizedLocalActDraft.buyer_name || 'Не заполнено'}
                            open={accordionSections.actBuyer}
                            onToggle={() => toggleAccordion('actBuyer')}
                          >
                            <div className="space-y-3">
                              <LabeledField label="Заказчик (кратко)">
                                <input
                                  type="text"
                                  value={normalizedLocalActDraft.buyer_name}
                                  onChange={(event) => setActField('buyer_name', event.target.value)}
                                  disabled={!canEdit}
                                  className={fieldClassName}
                                />
                              </LabeledField>
                              <div className="grid grid-cols-2 gap-2">
                                <LabeledField label="ИНН">
                                  <input
                                    type="text"
                                    value={normalizedLocalActDraft.buyer_inn}
                                    onChange={(event) => setActField('buyer_inn', event.target.value)}
                                    disabled={!canEdit}
                                    className={fieldClassName}
                                  />
                                </LabeledField>
                                <LabeledField label="КПП">
                                  <input
                                    type="text"
                                    value={normalizedLocalActDraft.buyer_kpp}
                                    onChange={(event) => setActField('buyer_kpp', event.target.value)}
                                    disabled={!canEdit}
                                    className={fieldClassName}
                                  />
                                </LabeledField>
                              </div>
                              <LabeledField label="Полное наименование и адрес">
                                <AutoResizeTextarea
                                  value={normalizedLocalActDraft.buyer_summary}
                                  onChange={(event) => setActField('buyer_summary', event.target.value)}
                                  disabled={!canEdit}
                                  minHeight={52}
                                  maxHeight={160}
                                  className={`${fieldClassName} text-xs leading-relaxed`}
                                  placeholder="Полное наименование организации, юр. адрес"
                                />
                              </LabeledField>
                            </div>
                          </DocumentAccordion>

                          {/* Аккордеон: Подписи в акте */}
                          <DocumentAccordion
                            title="Подписи в акте"
                            subtitle={`${normalizedLocalActDraft.contractor_name || '—'} / ${normalizedLocalActDraft.customer_name || '—'}`}
                            open={accordionSections.actSignatures}
                            onToggle={() => toggleAccordion('actSignatures')}
                          >
                            <div className="space-y-3">
                              <div className="grid grid-cols-2 gap-2">
                                <LabeledField label="Подпись исполнителя">
                                  <input
                                    type="text"
                                    value={normalizedLocalActDraft.contractor_name}
                                    onChange={(event) => setActField('contractor_name', event.target.value)}
                                    disabled={!canEdit}
                                    className={fieldClassName}
                                    placeholder="ФИО"
                                  />
                                </LabeledField>
                                <LabeledField label="Подпись заказчика">
                                  <input
                                    type="text"
                                    value={normalizedLocalActDraft.customer_name}
                                    onChange={(event) => setActField('customer_name', event.target.value)}
                                    disabled={!canEdit}
                                    className={fieldClassName}
                                    placeholder="ФИО"
                                  />
                                </LabeledField>
                              </div>
                            </div>
                          </DocumentAccordion>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Sticky Bottom Bar: Отдельная строка Итого + Отдельная строка Кнопка */}
          <footer className="sticky bottom-0 z-20 border-t border-gray-200 bg-white/98 px-3 py-2.5 shadow-[0_-4px_16px_rgba(0,0,0,0.06)] backdrop-blur-sm dark:border-gray-800 dark:bg-gray-900/98 sm:px-6 sm:py-3">
            <div className="mx-auto flex w-full max-w-7xl flex-col gap-2">
              {/* Строка 1: Итого */}
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-baseline gap-2 min-w-0">
                  <span className="text-xs font-medium text-gray-500 dark:text-gray-400 truncate">
                    {activeDocTab === 'act' ? 'Итого по акту:' : 'Итого к оплате:'}
                  </span>
                  <span className="text-base sm:text-lg font-bold text-gray-900 dark:text-gray-100 shrink-0">
                    {formatInvoiceMoney(activeDocTab === 'act' ? actRowsTotal : rowsTotal)} ₽
                  </span>
                </div>
                {isCompletedOrder && (
                  <span className="shrink-0 rounded-md bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-800 dark:bg-amber-950/40 dark:text-amber-300">
                    Фиксированная сумма
                  </span>
                )}
              </div>

              {!isAmountValid && (
                <div className="text-xs font-semibold text-red-600 dark:text-red-400">
                  Сумма должна строго совпадать с суммой заказа: {formatInvoiceMoney(expectedAmount)} ₽
                </div>
              )}

              {/* Строка 2: Кнопка во всю ширину */}
              <div>
                {canEdit ? (
                  <button
                    type="button"
                    onClick={handleSave}
                    disabled={!canSave}
                    className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-xs transition-all hover:bg-blue-700 active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <Check className="h-4 w-4" />
                    <span>{saving ? 'Сохраняем...' : 'Сохранить пакет документов'}</span>
                  </button>
                ) : (
                  <div className="flex h-10 w-full items-center justify-center rounded-xl border border-gray-200 bg-gray-50 text-xs font-medium text-gray-500 dark:border-gray-800 dark:bg-gray-850 dark:text-gray-400">
                    Режим просмотра
                  </div>
                )}
              </div>
            </div>
          </footer>
        </div>
      </ModalShell>

      {/* Модальное окно выбора единиц измерения */}
      <UnitPickerModal
        open={Boolean(unitPickerTarget)}
        onClose={() => setUnitPickerTarget(null)}
        currentUnit={unitPickerTarget?.currentUnit || 'усл.'}
        onSelect={handleUnitSelected}
      />

      {/* Всплывающие уведомления (наш стандартный тост — смахивается, сам пропадает, без крестика) */}
    </>
  );
};

export default InvoiceEditorModal;
